import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { readingOf, type ReadingDefault, type ReadingPart } from '../../stores/slices/progress';
import type { DefaultItem, LadderAnswer } from '../Ladder/claims';
import { DefaultChips, defaultsChangeable } from './DefaultsLine';

/** A part of the reading: a word in a caption, with a `·` before it. A chip underneath, so a word can still open its menu. */
export const CHIP_SX = {
  height: 'auto', border: 0, borderRadius: 0, color: 'var(--ink-3)', bgcolor: 'transparent', fontSize: 12.5, maxWidth: '100%',
  '& .MuiChip-label': { px: 0 },
  '&:not(:first-of-type)::before': { content: '"·"', mr: 0.75, color: 'var(--ink-4, var(--ink-3))' },
  '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus-visible': { bgcolor: 'transparent', color: 'var(--ink)' },
  '&.MuiChip-clickable .MuiChip-label': { textDecoration: 'underline dotted', textUnderlineOffset: '3px', textDecorationColor: 'var(--ink-4, var(--ink-3))' },
} as const;

/** The perspective change the reading offers (Q2): through #767's re-ask; a change while the turn runs stops it first,
 *  then re-asks in its place. */
export function usePerspectiveChange(turn: LandingAgentTurn, current: string) {
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const reaskInPerspective = useVoyagerStore((s) => s.reaskInPerspective);
  const cancel = useVoyagerStore((s) => s.cancelLandingAgent);
  const stopRun = useVoyagerStore((s) => s.stopBackgroundAnalysis);
  const running = turn.status === 'running';
  // while the turn runs, a change replaces it in place, which names its session: none before the plan, so no menu yet
  const changeable = !!turn.askedRequest && (!running || !!turn.sessionId);
  const choices = [{ id: 'auto', name: 'Auto' }, ...(perspectives || []).map((p) => ({ id: p.id, name: p.name || p.id }))]
    .filter((c) => c.id !== current);
  const change = (id: string) => {
    if (!running) { reaskInPerspective(turn, id, 'thread'); return; }
    // stop THIS turn (it may not be the composer's), then re-ask it as a Change of its own route, in its place
    if (turn.analysisOperationId) void stopRun(turn.id); else cancel();
    const route = (turn.result?.ladder as LadderAnswer | null | undefined)?.route?.route || 'data';
    reaskInPerspective(turn, id, 'thread', { replaces: turn.sessionId || null, route });
  };
  return { changeable, choices, change };
}

/** The perspective chip (Q2). */
function PerspectiveChoice({ turn, part }: { turn: LandingAgentTurn; part: ReadingPart }) {
  const { changeable, choices, change } = usePerspectiveChange(turn, part.perspective.id);
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  return (
    <>
      <Chip size="small" variant="outlined" data-testid="reading-chip-perspective"
            label={part.perspective.label}
            aria-haspopup={changeable ? 'menu' : undefined}
            onClick={changeable ? (e) => setAnchor(e.currentTarget) : undefined} sx={CHIP_SX} />
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {choices.map((c) => <MenuItem key={c.id} onClick={() => { setAnchor(null); change(c.id); }}>{c.name}</MenuItem>)}
      </Menu>
    </>
  );
}

function PartChips({ turn, part, lead }: { turn: LandingAgentTurn; part: ReadingPart; lead: boolean }) {
  // F3's items (#1808): their chips in place of the plain ones they decided — with F3's menu where its contract holds
  // (the ladder's block, once the answer stands), plain while the turn runs
  const block = (turn.result?.ladder as LadderAnswer | null | undefined)?.defaults;
  const decided = (part.defaults || []).map((d) => {
    const held = block?.items?.find((i) => i.id === d.id);
    return held ? { ...d, ...held } : d;
  });
  const changeable = defaultsChangeable(turn) && decided.every((d) => block?.items?.some((i) => i.id === d.id));
  return (
    <Box data-testid={`reading-part-${part.sub_task_id}`}
         sx={{ display: 'flex', alignItems: 'center', columnGap: 0.75, flexWrap: 'wrap', minWidth: 0,
           opacity: part.notAnswered ? 0.6 : 1 }}>
      {lead && <Box sx={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
        {part.metrics.map((m) => m.label).join(' and ')}</Box>}
      {!lead && part.metrics.map((m) => (
        <Chip key={m.id} size="small" variant="outlined" data-testid="reading-chip-metric"
              label={m.draft ? `${m.label} · Draft` : m.label} sx={CHIP_SX} />
      ))}
      {part.grouping.map((g) => (
        <Chip key={g.id} size="small" variant="outlined" data-testid="reading-chip-grouping" label={`by ${g.label}`} sx={CHIP_SX} />
      ))}
      {part.window && <Chip size="small" variant="outlined" data-testid="reading-chip-window" label={part.window.label} sx={CHIP_SX} />}
      {part.filters.map((f, i) => (
        <Chip key={i} size="small" variant="outlined" data-testid="reading-chip-filter" label={f.label} sx={CHIP_SX} />
      ))}
      {decided.length > 0 && <DefaultChips turn={turn} items={decided} changeable={changeable} sx={CHIP_SX} />}
      <PerspectiveChoice turn={turn} part={part} />
      {part.notAnswered && <Box component="span" data-testid="reading-not-answered"
                                sx={{ fontSize: 12, color: 'var(--ink-3)' }}>not answered</Box>}
    </Box>
  );
}

/** Whether the reading line shows any of F3's items (#1808) — the defaults line then keeps only its sentences. */
export function readingShowsDefaults(turn: LandingAgentTurn): boolean {
  return readingOf(turn).some((p) => (p.defaults || []).length > 0);
}

/**
 * F4 (#1858 §2): "Reading this as" — the chips of what the question was understood as, from the plan's decision and
 * then from what ran, part by part; "Read as" once the answer stands. Every word is the server's, rendered for this
 * reader; one part is one line, two or more are one line each, led by the part's metric.
 */
export default function ReadingLine({ turn }: { turn: LandingAgentTurn }) {
  const parts = readingOf(turn);
  if (!parts.length) return null;
  const running = turn.status === 'running';
  const many = parts.length > 1;
  return (
    <Box data-testid="reading-line" sx={{ display: 'flex', flexDirection: many ? 'column' : 'row', alignItems: many ? 'stretch' : 'center',
      gap: many ? 0.25 : 0.75, flexWrap: 'wrap', minWidth: 0 }}>
      <Box component="span" data-testid="reading-head" sx={{ fontSize: 12.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
        {running ? 'Reading this as' : 'Read as'}
      </Box>
      {parts.map((part) => <PartChips key={part.sub_task_id} turn={turn} part={part} lead={many} />)}
    </Box>
  );
}

/** The context line's small dropdown buttons (the reading, the period). */
export const DD_SX = {
  height: 28, px: 1.125, gap: 0.5, border: '1px solid var(--line)', borderRadius: '7px', bgcolor: 'var(--panel, var(--surface))',
  color: 'var(--ink-2)', fontSize: 12.5, maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  '&:hover, &[aria-expanded="true"]': { borderColor: 'var(--ink-3)', color: 'var(--ink)' },
  '&[aria-disabled="true"]': { cursor: 'default', '&:hover': { borderColor: 'var(--line)', color: 'var(--ink-2)' } },
} as const;

/** The same look on F3's chips (`DefaultChips` with `sx`): a bordered, quiet button. */
const DD_CHIP_SX = {
  height: 28, borderRadius: '7px', border: '1px solid var(--line)', bgcolor: 'var(--panel, var(--surface))', color: 'var(--ink-2)',
  fontSize: 12.5, fontWeight: 400, maxWidth: '100%',
  '& .MuiChip-label': { px: 1.125, display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 400 },
  '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus-visible': { bgcolor: 'var(--panel, var(--surface))', borderColor: 'var(--ink-3)', color: 'var(--ink)' },
} as const;

const CHEVRON = <ExpandMoreIcon aria-hidden sx={{ fontSize: 14, flexShrink: 0 }} />;

/** A reading part's words, once: its metrics, its grouping and its filters (the perspective is the menu's). */
export function partLabel(part: ReadingPart, reading?: string | null): string {
  const metrics = reading || part.metrics.map((m) => (m.draft ? `${m.label} · Draft` : m.label)).join(' and ');
  return [metrics, ...part.grouping.map((g) => `by ${g.label}`), ...part.filters.map((f) => f.label)].filter(Boolean).join(' · ');
}

/** What a reading item read the word as: its current option, else its own label. */
const readingWords = (items: DefaultItem[]) =>
  items.map((r) => (r.options || []).find((o) => o.current)?.label || r.label).filter(Boolean).join(' · ');

/** One part's reading as a dropdown: the reading items' options (F3's `default_change`) and the perspective (Q2's re-ask). */
function ReadingButton({ turn, part, readings, changeable }: {
  turn: LandingAgentTurn; part: ReadingPart | null; readings: DefaultItem[]; changeable: boolean;
}) {
  const changeDefault = useVoyagerStore((s) => s.changeDefault);
  const perspective = usePerspectiveChange(turn, part?.perspective.id || '');
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const options = changeable ? readings.filter((r) => (r.options || []).length > 0) : [];
  const perspectives = part && perspective.changeable ? perspective.choices : [];
  const can = options.length > 0 || perspectives.length > 0;
  const label = part ? partLabel(part, readingWords(readings)) : readingWords(readings);
  const title = [...readings.map((r) => r.sentence), part ? `From ${part.perspective.label}` : null].filter(Boolean).join(' ');
  const close = () => setAnchor(null);
  return (
    <>
      <ButtonBase data-testid={part ? `read-as-${part.sub_task_id}` : 'read-as-reading'} title={title || undefined}
        aria-label={`Read as ${label}`} aria-haspopup={can ? 'menu' : undefined} aria-expanded={can ? !!anchor : undefined}
        aria-disabled={!can} onClick={can ? (e) => setAnchor(e.currentTarget) : undefined}
        sx={{ ...DD_SX, opacity: part?.notAnswered ? 0.6 : 1 }}>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
        {can && CHEVRON}
      </ButtonBase>
      {part?.notAnswered && <Box component="span" data-testid="reading-not-answered" sx={{ fontSize: 12, color: 'var(--ink-3)' }}>not answered</Box>}
      <Menu anchorEl={anchor} open={!!anchor} onClose={close} MenuListProps={{ dense: true }}>
        {options.flatMap((item) => [
          <ListSubheader key={`${item.id}-head`} sx={{ lineHeight: '28px', fontSize: 11.5, color: 'var(--ink-3)' }}>{item.label}</ListSubheader>,
          ...item.options.map((o) => (
            <MenuItem key={`${item.id}-${o.option}`} selected={!!o.current} disabled={!!o.current}
              data-testid={`default-option-${item.id}-${o.option}`} onClick={() => { close(); changeDefault(turn.id, item.id, o.option); }}>
              {o.label}
            </MenuItem>
          )),
        ])}
        {perspectives.length > 0 && <ListSubheader key="p-head" sx={{ lineHeight: '28px', fontSize: 11.5, color: 'var(--ink-3)' }}>Answer from</ListSubheader>}
        {perspectives.map((c) => (
          <MenuItem key={`p-${c.id}`} data-testid={`read-as-perspective-${c.id}`} onClick={() => { close(); perspective.change(c.id); }}>{c.name}</MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * A finished data answer's context line (the answer card's own line, replacing "Read as" and the defaults line): the
 * reading as a small dropdown, its label once, the period (F3's window item, its Change as on the defaults line), the
 * other F3 items, then the short note the defaults said. Only the presentation is new: every Change is the reading
 * line's and the defaults line's own.
 */
export function ReadAsLine({ turn }: { turn: LandingAgentTurn }) {
  const parts = readingOf(turn);
  const block = (turn.result?.ladder as LadderAnswer | null | undefined)?.defaults;
  const held: DefaultItem[] = (block?.items || []).filter((i) => i && typeof i.sentence === 'string');
  const changeable = defaultsChangeable(turn);
  // the block's window item for a part: the one naming its sub-task (one per window, `window:<first spec>`), else one
  // of the part's own ids that names no sub-task at all — an item naming others (a part not answered) is never this one's
  const windowFor = (part: ReadingPart) =>
    held.find((i) => i.kind === 'window' && (i.specs || []).includes(part.sub_task_id))
    || held.find((i) => i.kind === 'window' && !(i.specs || []).length && (part.defaults || []).some((d) => d.id === i.id));
  // a part's own items the block does not hold: shown as the reading line shows them, with no menu; a window is the
  // part's period (below), never a loose chip
  const loose = parts.flatMap((p) => (p.defaults || []).filter((d) => d.kind !== 'window' && !held.some((i) => i.id === d.id)));
  const unique = (items: ReadingDefault[]) => items.filter((d, i) => items.findIndex((x) => x.id === d.id) === i);
  const readings = held.filter((i) => i.kind === 'reading');
  const windows = held.filter((i) => i.kind === 'window');
  const others = held.filter((i) => i.kind !== 'reading' && i.kind !== 'window');
  // each part's period: F3's window item it decided (with its Change), else its own live window default, else the
  // window it ran over (both plain)
  const periodOf = (part: ReadingPart) => {
    const item = windowFor(part);
    if (item) return { label: item.label, item };
    const own = (part.defaults || []).find((d) => d.kind === 'window');
    const label = own?.label || part.window?.label;
    return label ? { label, item: null } : null;
  };
  const periods = parts.map(periodOf);
  // one period chip only when every part ran over the same one; otherwise each part keeps its own beside its reading
  const shared = periods.every((p) => (p?.label ?? null) === (periods[0]?.label ?? null));
  const tied = new Set(periods.flatMap((p) => (p?.item ? [p.item.id] : [])));
  const untied = windows.filter((w) => !tied.has(w.id));
  // the same period on two items (one per group of parts) is one chip
  const onePerLabel = windows.filter((w, i) => windows.findIndex((x) => x.label === w.label) === i);
  const plainWindow = shared && !windows.length ? periods.find((p) => p)?.label ?? null : null;
  const period = (p: ReturnType<typeof periodOf>, key: string) => (!p ? null : p.item
    ? <DefaultChips key={key} turn={turn} items={[p.item]} changeable={changeable} sx={DD_CHIP_SX} chevron />
    : <Box key={key} component="span" data-testid="read-as-window" sx={{ ...DD_SX, display: 'inline-flex', alignItems: 'center' }}>{p.label}</Box>);
  const sentences = held.map((i) => i.sentence).filter(Boolean);
  if (!parts.length && !held.length && !loose.length && !turn.defaultsNotice) return null;
  return (
    <Box data-testid="read-as-line" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0, fontSize: 12.5, color: 'var(--ink-3)' }}>
      {parts.map((part, i) => (
        <React.Fragment key={part.sub_task_id}>
          <ReadingButton turn={turn} part={part} readings={i === 0 ? readings : []} changeable={changeable} />
          {!shared && period(periods[i], `period-${part.sub_task_id}`)}
        </React.Fragment>
      ))}
      {!parts.length && readings.length > 0 && <ReadingButton turn={turn} part={null} readings={readings} changeable={changeable} />}
      {(shared ? onePerLabel : untied).length > 0 && <DefaultChips turn={turn} items={shared ? onePerLabel : untied} changeable={changeable} sx={DD_CHIP_SX} chevron />}
      {plainWindow && period({ label: plainWindow, item: null }, 'period')}
      {others.length > 0 && <DefaultChips turn={turn} items={others} changeable={changeable} sx={DD_CHIP_SX} chevron />}
      {loose.length > 0 && <DefaultChips turn={turn} items={unique(loose)} changeable={false} sx={DD_CHIP_SX} />}
      {sentences.length > 0 && (
        <Box component="span" data-testid="defaults-said" sx={{ minWidth: 0, overflowWrap: 'anywhere', ml: 0.25 }}>{sentences.join(' ')}</Box>
      )}
      {turn.defaultsNotice && <Box component="span" data-testid="defaults-notice">{turn.defaultsNotice}</Box>}
    </Box>
  );
}
